<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import { IBranch } from '../../../interfaces/IBranch';
  import BranchesService from '../../../api/services/branches.service';
  import IEmail from '../../../interfaces/IEmail';
  import { vueData } from '../../../VueData';
  import EmailStatus from '../../../enums/EmailStatus';
  import { IOrder } from '../../../interfaces/IOrder';
  import OrderModal from '../../modals/OrderModal.vue';
  import { OrderPaymentStatus, OrderStatus } from '../../../enums/OrderEnums';
  import OrdersService from '../../../api/services/orders.service';
  import { PermissionType } from '../../../enums/PermissionType';

  @Component
  export default class OrderEmailSent extends Vue {
    @Prop() branch: IBranch;
    branchesService = new BranchesService();
    ordersService = new OrdersService();
    emails: IEmail[] | null = null;
    loading = false;
    options = {
      itemsPerPage: 10,
      page: 1,
      sortBy: ['createdAt'],
      sortDesc: [true],
    };
    headers = [
      { value: 'createdAt', text: '' },
      { value: 'fromAddress', text: 'Email (desde)' },
      { value: 'toAddress', text: 'Email (para)' },
      { value: 'order', text: 'Pedido' },
      { value: 'status', text: 'Estado' },
    ];
    totalCount = 0;

    async created() {
      await this.loadEmails();
    }

    async loadEmails() {
      try {
        this.loading = true;
        const emails = await this.branchesService.getEmails(
          this.branch.id,
          this.options
        );
        this.emails = emails.items;
        this.totalCount = emails.total;
      } catch (e) {
        vueData.serverError(e);
        console.error(e);
        this.emails = null;
      } finally {
        this.loading = false;
      }
    }

    getStatusColor(status: EmailStatus) {
      return status === EmailStatus.Sent ? 'green' : 'red';
    }

    getStatusName(status: EmailStatus) {
      switch (status) {
        case EmailStatus.Sent:
          return 'Enviado';
        case EmailStatus.Failed:
          return 'Falló';
      }
    }

    /** GET /orders/:id requires an orders permission; hide the action otherwise. */
    get canViewOrder() {
      return (
        vueData.hasPermission(PermissionType.OrdersHistoric) ||
        vueData.hasPermission(PermissionType.OrdersManage)
      );
    }

    async view(orderId: number) {
      const fullOrder = await OrdersService.getById(orderId);

      this.$modal.show(
        OrderModal,
        {
          order: fullOrder,
          printEnabled: false,
          hideActions: true,
        },
        {
          name: 'view-order',
          classes: 'dialog--height-limit',
          adaptive: true,
          height: 'auto',
          width: 1000,
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          transition: 'none',
        }
      );
    }
  }
</script>

<template>
  <div style="position: relative">
    <v-skeleton-loader type="paragraph" v-if="!emails" />
    <template v-else>
      <v-overlay :value="loading" :absolute="'absolute'">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>

      <v-data-table
        :must-sort="true"
        :options.sync="options"
        :headers="headers"
        disable-sort
        :items="emails"
        :server-items-length="totalCount"
        :items-per-page="options.itemsPerPage"
        :sort-by="options.sortBy"
        :sort-desc="options.sortDesc"
        :calculate-widths="true"
        :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
        @update:options="loadEmails"
        :dense="true"
      >
        <template v-slot:item.createdAt="{ item }">
          <timeago
            class="font-size-9e grey--text"
            :datetime="item.createdAt"
            :auto-update="60"
          ></timeago>
        </template>
        <template v-slot:item.fromAddress="{ item }">
          <span class="grey--text">{{ item.fromAddress }}</span>
        </template>
        <template v-slot:item.order="{ item }">
          <v-btn
            v-if="canViewOrder"
            x-small
            color="info"
            depressed
            @click="view(item.orderId)"
            >Ver pedido</v-btn
          >
          <span v-else class="grey--text">#{{ item.orderId }}</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" small label dark>{{
            getStatusName(item.status)
          }}</v-chip>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<style scoped lang="scss"></style>
